import { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { MainContent, ActionHeader, PageInfo } from 'components';
import { useForm, useDebounce } from 'hooks';
import {
  getUsers,
  deleteUser,
  createUser,
  updateUser,
  getRoles,
} from 'services';
import { IUser, OptionsType } from 'types';
import { listUserSelector, profileSelector } from 'modules';
import FormUser from './FormUser';
import TableUser from './TableUser';
import { validator, isAuthenticated, PERMISSION_LABELS } from 'utils';
import { popupMessage, errorBag, loadingScreen, confirmation } from 'controls';
import { ActionMeta, SingleValue } from 'react-select';

export type FormState = {
  id: string;
  name: string;
  email: string;
  roleId: string;
  status: string;
  password: string;
  passwordConfirmation: string;
};

export type ErrorState = {
  name: string;
  email: string;
  roleId: string;
  status: string;
  password: string;
  passwordConfirmation: string;
};

const initialFormState: FormState = {
  id: '',
  name: '',
  email: '',
  roleId: '',
  status: '',
  password: '',
  passwordConfirmation: '',
};

const initialErrorState: ErrorState = {
  name: '',
  email: '',
  roleId: '',
  status: '',
  password: '',
  passwordConfirmation: '',
};

function User(): JSX.Element {
  // state

  const profile = useSelector(profileSelector, shallowEqual);
  const listUser = useSelector(listUserSelector, shallowEqual);

  const [loadingForm, setLoadingForm] = useState<boolean>(false);
  const [formUser, setFormUser] = useState<boolean>(false);
  const [form, setForm] = useForm(initialFormState);
  const [error, setError] = useState(initialErrorState);
  const [searchParams, setSearchParam] = useSearchParams();

  const defaultLimit = 10;
  const page = searchParams.get('page') || 0;
  const limit = searchParams.get('limit') || defaultLimit;
  const search = searchParams.get('search') || '';

  const debouncedSearchQuery = useDebounce(search, 500);

  const users = listUser.data || [];
  const isLogin = isAuthenticated();

  // variable
  const isEdit = Boolean(form.id);

  function onChangePage(e: React.MouseEvent<HTMLAnchorElement>): void {
    const dataPage = e.currentTarget.getAttribute('data-page');
    const pageNumber = Number(dataPage);
    const urlParams = new URLSearchParams();
    urlParams.set('page', String(pageNumber));

    if (limit) {
      urlParams.set('limit', String(limit));
    }
    setSearchParam(urlParams);
  }

  function onChangeFormInput(e: React.ChangeEvent<HTMLInputElement>): void {
    const { name, value } = e.target;
    setForm(name, value);
    const field = name as keyof typeof error;

    // eslint-disable-next-line security/detect-object-injection
    if (error[field] !== '') {
      setError((prev) => ({ ...prev, [field]: '' }));
    }
  }

  function onChangeSelectInput(
    selectValue: SingleValue<OptionsType>,
    meta: ActionMeta<OptionsType>,
  ): void {
    const value = selectValue?.value;
    setForm(meta.name, value);
    const field = meta.name as keyof typeof error;

    // eslint-disable-next-line security/detect-object-injection
    if (error[field] !== '') {
      setError((prev) => ({ ...prev, [field]: '' }));
    }
  }

  function onCloseForm(): void {
    setFormUser(false);
    setForm('reset', null);
    setError(initialErrorState);
  }

  function onAdd(): void {
    setFormUser(true);
    getRoles({
      page: 0,
      limit: 25,
    });
  }

  function onEdit(data: IUser): void {
    setForm(
      {
        id: data.id,
        name: data.name,
        email: data.email,
        status: data.status.toUpperCase(),
        roleId: data.role.id,
        password: '',
        passwordConfirmation: '',
      },
      null,
    );
    setFormUser(true);
    getRoles({
      page: 0,
      limit: 25,
    });
  }

  function confirmDelete(data: IUser): void {
    loadingScreen(true);
    deleteUser(data.id)
      .then(function () {
        loadingScreen(false);
        popupMessage({
          show: true,
          type: 'success',
          title: 'Berhasil Dihapus',
          message: 'Hi, User berhasil dihapus',
        });
        getUsers({
          page: 0,
          limit: 25,
        });
      })
      .catch(function (err) {
        errorBag(err);
      });
  }

  function onDelete(data: IUser): void {
    confirmation({
      title: 'Mau Hapus?',
      onConfirmed: () => confirmDelete(data),
    });
  }

  function validateForm(): [boolean, ErrorState] {
    const newError = { ...error };

    if (!form.name) {
      newError.name = 'Mohon masukan nama user';
    }

    if (!form.email) {
      newError.email = 'Mohon masukan email';
    } else if (!validator.isEmail(form.email)) {
      newError.email = 'Format email tidak valid';
    }
    if (!form.roleId) {
      newError.roleId = 'Mohon pilih role';
    }

    if (!form.status) {
      newError.status = 'Mohon pilih status';
    }

    if (!isEdit) {
      if (!form.password) {
        newError.password = 'Masukan password';
      } else if (!validator.min(form.password, 8)) {
        newError.password = 'Password minimal 8 karakter';
      }

      if (!form.passwordConfirmation) {
        newError.passwordConfirmation = 'Masukan konfirmasi password';
      } else if (form.passwordConfirmation !== form.password) {
        newError.passwordConfirmation = 'Kombinasi password tidak sama';
      }
    }

    const invalid = Object.values(newError).some((e) => e !== '');
    return [!invalid, newError];
  }

  async function onSubmit(e: React.SyntheticEvent): Promise<void> {
    e?.preventDefault();

    const [valid, newError] = validateForm();
    if (!valid) {
      setError(newError);
      return;
    }

    try {
      setLoadingForm(true);
      if (isEdit) {
        await updateUser({
          id: form.id,
          name: form.name,
          email: form.email,
          roleId: form.roleId,
          status: form.status,
          password: form.password,
        });
      } else {
        await createUser({ ...form });
      }
      setLoadingForm(false);
      onCloseForm();

      popupMessage({
        show: true,
        type: 'success',
        title: isEdit ? 'Berhasil Diubah' : 'Berhasil Dibuat',
        message: isEdit
          ? 'Hi, Data User berhasil diubah'
          : 'Hi, User baru berhasil dibuat',
      });

      getUsers({
        page: Number(page),
        limit: Number(limit),
        ...(search && {
          search: {
            keyword: search,
          },
        }),
      });
    } catch (err) {
      setLoadingForm(false);
      errorBag(err);
    }
  }

  function onSearch(e: React.ChangeEvent<HTMLInputElement>): void {
    const { value } = e.target;

    const urlParams = new URLSearchParams();

    if (value) {
      urlParams.set('search', value);
    } else {
      urlParams.delete('search');
    }
    urlParams.set('page', String(0));
    if (limit) {
      urlParams.set('limit', String(defaultLimit));
    }
    setSearchParam(urlParams);
  }

  function onSubmitSearch(e: React.SyntheticEvent): void {
    e?.preventDefault();
  }

  useEffect(
    function () {
      if (profile && isLogin) {
        getUsers({
          search: {
            keyword: debouncedSearchQuery,
          },
          page: Number(page),
          limit: Number(limit),
        }).catch(function (err) {
          errorBag(err);
        });
      }
    },
    [profile, isLogin, debouncedSearchQuery, limit, page],
  );

  return (
    <>
      <PageInfo title={PERMISSION_LABELS.USER} />
      <MainContent
        title={PERMISSION_LABELS.USER}
        headerRightElement={
          <ActionHeader
            addTitle="Tambah User"
            searchPlaceholder="Search..."
            onAdd={onAdd}
            onChange={onSearch}
            onSubmit={onSubmitSearch}
            value={search}
          />
        }
      >
        <TableUser
          limit={Number(listUser.limit) || Number(limit)}
          page={Number(listUser.page) || Number(page)}
          totalPage={Number(listUser.total)}
          onChangePage={onChangePage}
          data={users}
          loading={listUser.loading}
          onDelete={onDelete}
          onEdit={onEdit}
        />
      </MainContent>
      <FormUser
        edit={isEdit}
        show={formUser}
        form={form}
        error={error}
        disabled={loadingForm}
        onSubmit={onSubmit}
        onChange={onChangeFormInput}
        onClose={onCloseForm}
        onSelectChange={onChangeSelectInput}
      />
    </>
  );
}

export default User;
